// src/Components/NavBar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiLogOut, FiUser, FiMapPin, FiUserPlus, FiLogIn, FiLayout, FiCalendar } from 'react-icons/fi';
import styles from './NavBar.module.css';
import logo from '../Assets/logoEmpresa.png';
import api from '../api/api';

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('currentUser');
    if (stored) {
      try { setCurrentUser(JSON.parse(stored)); }
      catch { setCurrentUser(null); }
    } else {
      setCurrentUser(null);
    }
    setShowNav(false);
  }, [location]);

  // Bloquear scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (showNav) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showNav]);

  const closeNav = () => setShowNav(false);

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch {
      // Ignorar errores de red; limpiar datos locales de todos modos
    }
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
    navigate('/login');
    closeNav();
  };

  const userName = currentUser?.user?.name || currentUser?.name || '';
  const userRole = currentUser?.role;
  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Overlay oscuro detrás del drawer */}
      {showNav && (
        <div className={styles.overlay} onClick={closeNav} />
      )}

      <nav className={styles.navBar}>
        <div className={styles.navContainer}>
          {/* Logo */}
          <div className={styles.brand} onClick={() => { navigate('/'); closeNav(); }}>
            <img src={logo} alt="Flamingo" className={styles.brandLogo} />
          </div>

          {/* Botón hamburguesa (solo mobile) */}
          <button className={styles.menuToggle} onClick={() => setShowNav(v => !v)} aria-label="Menú">
            <FiMenu size={22} />
          </button>

          {/* Links desktop */}
          <div className={styles.desktopLinks}>
            <Link to="/busroutes" className={`${styles.navLink} ${isActive('/busroutes') ? styles.navLinkActive : ''}`}>
              <FiMapPin size={16} />
              <span>Rutas</span>
            </Link>

            {!currentUser && (
              <>
                <Link to="/register" className={`${styles.navLink} ${isActive('/register') ? styles.navLinkActive : ''}`}>
                  <FiUserPlus size={16} />
                  <span>Registrarse</span>
                </Link>
                <Link to="/login" className={styles.loginButton}>
                  <FiLogIn size={16} />
                  <span>Iniciar Sesión</span>
                </Link>
              </>
            )}

            {currentUser && (
              <>
                {userRole === 'admin' && (
                  <Link to="/admin" className={`${styles.navLink} ${isActive('/admin') ? styles.navLinkActive : ''}`}>
                    <FiLayout size={16} />
                    <span>Panel Admin</span>
                  </Link>
                )}
                {userRole === 'employee' && (
                  <Link to="/employee" className={`${styles.navLink} ${isActive('/employee') ? styles.navLinkActive : ''}`}>
                    <FiLayout size={16} />
                    <span>Dashboard</span>
                  </Link>
                )}
                {(userRole === 'admin' || userRole === 'employee') && (
                  <Link to="/course-management" className={`${styles.navLink} ${isActive('/course-management') ? styles.navLinkActive : ''}`}>
                    <FiCalendar size={16} />
                    <span>Viajes</span>
                  </Link>
                )}
                <div className={styles.divider} />
                <Link to="/PerfilUsuario" className={`${styles.userButton} ${isActive('/PerfilUsuario') ? styles.navLinkActive : ''}`}>
                  <div className={styles.userAvatar}>
                    {userName ? userName.charAt(0).toUpperCase() : <FiUser size={14} />}
                  </div>
                  <span className={styles.userButtonName}>{userName || 'Perfil'}</span>
                </Link>
                <button onClick={handleLogout} className={styles.logoutButton}>
                  <FiLogOut size={16} />
                  <span>Salir</span>
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Drawer mobile */}
      <div className={`${styles.drawer} ${showNav ? styles.drawerOpen : ''}`}>
        {/* Cabecera del drawer */}
        <div className={styles.drawerHeader}>
          <img src={logo} alt="Flamingo" className={styles.drawerLogo} />
          <button className={styles.drawerClose} onClick={closeNav} aria-label="Cerrar menú">
            <FiX size={22} />
          </button>
        </div>

        {/* Items */}
        <div className={styles.drawerBody}>
          <Link to="/busroutes" className={`${styles.drawerLink} ${isActive('/busroutes') ? styles.drawerLinkActive : ''}`} onClick={closeNav}>
            <FiMapPin size={18} />
            <span>Rutas</span>
          </Link>

          {!currentUser && (
            <>
              <Link to="/register" className={`${styles.drawerLink} ${isActive('/register') ? styles.drawerLinkActive : ''}`} onClick={closeNav}>
                <FiUserPlus size={18} />
                <span>Registrarse</span>
              </Link>
              <Link to="/login" className={styles.drawerLoginButton} onClick={closeNav}>
                <FiLogIn size={18} />
                <span>Iniciar Sesión</span>
              </Link>
            </>
          )}

          {currentUser && (
            <>
              {userRole === 'admin' && (
                <Link to="/admin" className={`${styles.drawerLink} ${isActive('/admin') ? styles.drawerLinkActive : ''}`} onClick={closeNav}>
                  <FiLayout size={18} />
                  <span>Panel Admin</span>
                </Link>
              )}
              {userRole === 'employee' && (
                <Link to="/employee" className={`${styles.drawerLink} ${isActive('/employee') ? styles.drawerLinkActive : ''}`} onClick={closeNav}>
                  <FiLayout size={18} />
                  <span>Dashboard</span>
                </Link>
              )}
              {(userRole === 'admin' || userRole === 'employee') && (
                <Link to="/course-management" className={`${styles.drawerLink} ${isActive('/course-management') ? styles.drawerLinkActive : ''}`} onClick={closeNav}>
                  <FiCalendar size={18} />
                  <span>Viajes</span>
                </Link>
              )}

              <div className={styles.drawerDivider} />

              <Link to="/PerfilUsuario" className={`${styles.drawerLink} ${isActive('/PerfilUsuario') ? styles.drawerLinkActive : ''}`} onClick={closeNav}>
                <div className={styles.userAvatar}>
                  {userName ? userName.charAt(0).toUpperCase() : <FiUser size={14} />}
                </div>
                <span>{userName || 'Mi Perfil'}</span>
              </Link>

              <button onClick={handleLogout} className={styles.drawerLogout}>
                <FiLogOut size={18} />
                <span>Cerrar Sesión</span>
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NavBar;
